import React, { useEffect, useState } from 'react';
import api from './api';

// Cross-org superadmin view — only reachable by an account with IsPlatformAdmin
// set on its Users row (see App.js's NAV_TABS gating). Lets whoever manages
// client licenses set/see each org's seat limit from one place, without ever
// switching into that org's own login.
export default function PlatformAdminPage({ msalInstance }) {
  const [orgs, setOrgs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({}); // orgSlug -> input value while editing
  const [savingSlug, setSavingSlug] = useState(null);
  const [togglingSlug, setTogglingSlug] = useState(null);

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      const token = localStorage.getItem('authToken');
      return { Authorization: `Bearer ${token}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const accounts = msalInstance.getAllAccounts();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

  const fetchOrgs = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await api.get('/api/platform-admin/orgs', { headers });
      setOrgs(res.data);
    } catch (err) {
      console.error('Failed to load orgs:', err);
      setError('Failed to load organizations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrgs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (orgSlug) => {
    const raw = drafts[orgSlug];
    const maxUsers = raw === '' || raw === undefined ? null : Number(raw);
    if (maxUsers !== null && (!Number.isInteger(maxUsers) || maxUsers < 1)) {
      alert('Enter a whole number of at least 1, or leave blank for unlimited.');
      return;
    }
    setSavingSlug(orgSlug);
    try {
      const headers = await getAuthHeaders();
      await api.post(`/api/platform-admin/orgs/${orgSlug}/max-users`, { maxUsers }, { headers });
      setOrgs(prev => prev.map(o => o.orgSlug === orgSlug ? { ...o, maxUsers } : o));
      setDrafts(prev => { const next = { ...prev }; delete next[orgSlug]; return next; });
    } catch (err) {
      console.error('Failed to save seat limit:', err);
      alert('Failed to save. Try again.');
    } finally {
      setSavingSlug(null);
    }
  };

  const handleTogglePoReceiving = async (orgSlug, nextEnabled) => {
    setTogglingSlug(orgSlug);
    try {
      const headers = await getAuthHeaders();
      await api.post(`/api/platform-admin/orgs/${orgSlug}/po-receiving`, { enabled: nextEnabled }, { headers });
      setOrgs(prev => prev.map(o => o.orgSlug === orgSlug ? { ...o, poReceivingEnabled: nextEnabled } : o));
    } catch (err) {
      console.error('Failed to toggle PO Receiving:', err);
      alert('Failed to save. Try again.');
    } finally {
      setTogglingSlug(null);
    }
  };

  if (loading) return <div style={s.wrap}><div style={s.muted}>Loading organizations…</div></div>;
  if (error) return <div style={s.wrap}><div style={s.errorBox}>{error}</div></div>;

  return (
    <div style={s.wrap}>
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Super Admin — Client Orgs</h1>
          <p style={s.subtitle}>Seat limits per org. Blank = unlimited. Doesn't affect anything else about an org's setup.</p>
        </div>
      </div>

      <div style={s.table}>
        <div style={s.rowHead}>
          <div style={{ ...s.cell, flex: 2 }}>Organization</div>
          <div style={{ ...s.cell, width: 90, textAlign: 'center' }}>Provider</div>
          <div style={{ ...s.cell, width: 90, textAlign: 'center' }}>Users</div>
          <div style={{ ...s.cell, width: 160, textAlign: 'center' }}>Seat limit</div>
          <div style={{ ...s.cell, width: 90 }} />
          <div style={{ ...s.cell, width: 110, textAlign: 'center' }}>PO Receiving</div>
        </div>

        {orgs.map(o => {
          const draftValue = drafts[o.orgSlug] ?? (o.maxUsers ?? '');
          const isDirty = drafts[o.orgSlug] !== undefined;
          const overLimit = o.maxUsers != null && o.userCount > o.maxUsers;
          return (
            <div key={o.orgSlug} style={s.row}>
              <div style={{ ...s.cell, flex: 2 }}>
                <div style={s.orgName}>{o.orgName || o.orgSlug}</div>
                <div style={s.orgSlug}>{o.orgSlug}</div>
              </div>
              <div style={{ ...s.cell, width: 90, textAlign: 'center' }}>
                <span style={s.providerPill}>{o.authProvider === 'google' ? 'Google' : 'Microsoft'}</span>
              </div>
              <div style={{ ...s.cell, width: 90, textAlign: 'center', fontWeight: 600, color: overLimit ? '#c2410c' : '#111' }}>
                {o.userCount}
              </div>
              <div style={{ ...s.cell, width: 160, display: 'flex', justifyContent: 'center' }}>
                <input
                  type="number"
                  min={1}
                  placeholder="Unlimited"
                  value={draftValue}
                  onChange={e => setDrafts(prev => ({ ...prev, [o.orgSlug]: e.target.value }))}
                  style={s.limitInput}
                />
              </div>
              <div style={{ ...s.cell, width: 90 }}>
                <button
                  onClick={() => handleSave(o.orgSlug)}
                  disabled={!isDirty || savingSlug === o.orgSlug}
                  style={{ ...s.saveBtn, opacity: isDirty ? 1 : 0.4 }}
                >
                  {savingSlug === o.orgSlug ? '…' : 'Save'}
                </button>
              </div>
              <div style={{ ...s.cell, width: 110, textAlign: 'center' }}>
                <button
                  onClick={() => handleTogglePoReceiving(o.orgSlug, !o.poReceivingEnabled)}
                  disabled={togglingSlug === o.orgSlug}
                  style={o.poReceivingEnabled ? s.toggleOn : s.toggleOff}
                >
                  {togglingSlug === o.orgSlug ? '…' : o.poReceivingEnabled ? 'On' : 'Off'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  wrap: { maxWidth: 900, margin: '0 auto', padding: '2rem 0 3rem' },
  headerRow: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: '#111' },
  subtitle: { fontSize: 13, color: '#666', margin: '6px 0 0' },
  muted: { padding: '2rem', textAlign: 'center', color: '#888' },
  errorBox: { padding: '1rem 1.25rem', background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8 },
  table: { border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  rowHead: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', background: '#f9fafb',
    fontSize: 11, fontWeight: 700, color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
  },
  cell: { fontSize: 13.5 },
  orgName: { fontWeight: 600, color: '#111' },
  orgSlug: { fontSize: 12, color: '#9ca3af', marginTop: 2, fontFamily: 'ui-monospace, monospace' },
  providerPill: {
    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
    background: '#f3f4f6', color: '#4b5563',
  },
  limitInput: {
    width: 100, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6,
    fontSize: 13, textAlign: 'center',
  },
  saveBtn: {
    padding: '6px 14px', background: '#0078d4', color: '#fff', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
  },
  toggleOn: {
    padding: '6px 14px', background: '#166534', color: '#fff', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, minWidth: 44,
  },
  toggleOff: {
    padding: '6px 14px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #ddd',
    borderRadius: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, minWidth: 44,
  },
};
